import RenderManager from "../rendering/render-manager";
import { AssetQueue } from "./asset-library";
import ensureArray from '../utils/ensure-array';
import createImage from "../utils/create-image";

const regexMap = /map$/i;
const regexIntensity = /Intensity/;
const validMaps = new Array(
    "map",
    "metalnessMap",
    "normalMap",
    "bumpMap",
    "roughnessMap",
    "aoMap",
    "alphaMap",
    "emissiveMap"
);

/**
 * Loads texture.
 * @param {Object} props Parameter.
 * @param {String} props.uuid Texture UUID.
 * @param {Object} props.data Texture data.
 * @param {THREE.Material[]} props.materials Material list.
 * @param {Object[]} props.slides Slides.
 * @return {Promise}
 */
async function loadTexture({ uuid, data, materials, slides }) {
    let texture;

    if (!!data.cube) texture = await loadCubeTexture(data);
    else if (!shouldIgnoreTexture(uuid, materials, slides))
        texture = await loadRegularTexture(data);
    else texture = null;

    if (texture) texture.uuid = uuid;

    return { uuid, texture };
}

/**
 * Adds texture to queue.
 * @param {Object} props Parameter.
 * @param {boolean} props.isAsset Is this an asset.
 * @param {number} props.size Size of the texture.
 * @param {string} props.uuid Texture UUID.
 * @param {Object} props.texture Texture data.
 * @param {AssetQueue} props.queue Texture queue.
 * @param {AssetQueue} props.queueLOD Texture LOD queue.
 * @param {THREE.Mesh} props.object Object to which material belongs.
 */
function addTextureToQueue({ isAsset, size, uuid, texture, queue, queueLOD, object, priority = 10 }) {
    const path = getTextureUrl(uuid, texture, size);
    if (Q3.editMode || size === 7)
        queue.addItem(priority, { uuid, type: "texture", data: { name: texture.name, path, object } }, params => loadQueueTexture(params));
    else queueLOD.addItem(priority, { uuid, type: "texture", data: { name: texture.name, path, object } }, params => loadQueueTexture(params));
}

/**
 * Adds background texture to queue.
 * @param {Object} props Parameter.
 * @param {String} props.uuid Texture UUID.
 * @param {Object} props.texture Texture data.
 * @param {Object} props.queue Texture queue.
 * @param {number} props.size Texture size.
 */
function addBackgroundTextureToQueue({ uuid, texture, queue, size }) {
    const path = getTextureUrl(uuid, texture, size);

    queue.addItem(-1, {
        uuid,
        type: "backgroundTexture",
        data: { name: texture.name, path }
    }, params => loadQueueTexture(params));
}

/**
 * Enques textures.
 * @param {Object} props Parameter.
 * @param {Object} props.data Project data.
 * @param {AssetQueue} props.queue Texture queue.
 * @param {AssetQueue} props.queueLOD Texture LOD queue.
 * @param {number} props.queuePriority Priority queue.
 * @param {boolean} props.isAsset Is this an asset.
 */
function enqueueTextures({ data, queue, queueLOD, queuePriority, isAsset }) {
    // const materials = Object.keys(data.materials).map(x => data.materials[x]);
    // const pbrTextures = materials.map(x => x.metalnessMap)
    //     .concat(materials.map(x => x.normalMap))
    //     .concat(materials.map(x => x.roughnessMap))
    //     .filter((item, pos, self) => self.indexOf(item) === pos);

    let backgroundTextures;

    if (Q3.editMode) {
        const bgTex = Q3.data.slides.map(slide => slide.background.value.image.texture);
        const bgMobTex = Q3.data.slides.map(slide => slide.background.value.image.textureMobile);
        backgroundTextures = bgTex.concat(bgMobTex);
    } else {
        if (Q3.browserCapabilities.isMobile)
            backgroundTextures = Q3.data.slides.map(slide => slide.background.value.image.textureMobile);
        else backgroundTextures = Q3.data.slides.map(slide => slide.background.value.image.texture);
    }

    Object.keys(data.textures).forEach(key => {
        const texture = data.textures[key];

        let pickedQueue = queue;

        //if texture placeholder was not created it means that we want to skip this texture (lq mode for instance)
        if (texture && !texture.cube) {
            const isBackground = backgroundTextures.includes(key);

            if (isBackground) pickedQueue = queuePriority;

            let size = texture.size || 0; //assume size 2^12 = 4k if not present
            const isMap = key === "map";

            if (Q3.browserCapabilities.supports.textures.lodEnabled) {
                if (size < 7) size = 0
                else {
                    if (size > 10) size = 10;
                    else if (size > 7) size = size - 1;

                    if (!isMap && size > 7 && Q3.browserCapabilities.supports.textures.lowResolution)
                        size = Math.max(7, size - 2);
                }
            }

            addTextureToQueue({ isAsset, uuid: key, texture, size, queue: pickedQueue, queueLOD });

            function enqueLayer(layer) {
                if (layer.map) {
                    const texture = data.assets.textures[layer.map];
                    addTextureToQueue({ queue, uuid: layer.map, isAsset, size: 0, texture, object, queueLOD });
                }

                if (layer.normalMap) {
                    const texture = data.assets.textures[layer.normalMap];
                    addTextureToQueue({ queue, uuid: layer.normalMap, isAsset, size: 0, texture, object, queueLOD });
                }
            }

            if (typeof material !== 'undefined') {
                if (material.layers && material.isMeshNestedMaterial)
                    material.layers.forEach(enqueLayer);
            }
        }
    });
}

/**
 * Collect textures.
 * @param {Object} props Parameter.
 * @param {Object} props.data Project data.
 * @param {AssetQueue} props.queue Texture queue.
 * @param {AssetQueue} props.queueLOD Texture LOD queue.
 * @param {string} props.key Texture UUID.
 * @param {boolean} props.isAsset Is this an asset.
 * @param {THREE.Mesh} props.object Object to which material belongs.
 * @param {string} props.objectUuid Object UUID.
 */
function collectTextures({ data, queue, queueLOD, key: materialUuid, isAsset, object, objectUuid, assets }) {
    const material = data.assets.materials[materialUuid];

    function enqueTexture(container, keyMap) {
        const texture = data.assets.textures[container[keyMap]];

        if (!texture) return;

        let size = texture.size || 0; //assume size 2^12 = 4k if not present
        const isMap = keyMap === "map";
        const isNormalMap = keyMap === "normalMap" || keyMap === "bumpMap";

        if (!Q3.editMode && isNormalMap && material.materialType !== "basic" && Q3.browserCapabilities.supports.pbr.normal.enabled) {
            const tex = assets.materials[materialUuid][keyMap];
            setDefaultNormalTexture(tex);
        }

        if (Q3.browserCapabilities.supports.textures.lodEnabled) {
            if (size < 7) size = 0
            else {
                if (size > 10) size = 10;
                else if (size > 7) size = size - 1;

                if (!isMap && size > 7 && Q3.browserCapabilities.supports.textures.lowResolution) {
                    size = size - 1;
                }
            }
        }

        if (!Q3.editMode && size > 7 && isMap)
            addTextureToQueue({ priority: 0, queue, queueLOD, uuid: container[keyMap], isAsset, size: 7, texture, object });
        addTextureToQueue({ priority: isMap ? 10 : 20, queue, queueLOD, uuid: container[keyMap], isAsset, size, texture, object });
    }

    function enqueLayer(layer) {
        if (layer.map) {
            const texture = data.assets.textures[layer.map];

            if (texture) addTextureToQueue({ queue, queueLOD, uuid: layer.map, isAsset, size: 0, texture, object });
            else console.warn("Malformed Q3 saved, missing texture:", layer.map)
        }

        if (layer.normalMap) {
            const texture = data.assets.textures[layer.normalMap];

            if (texture) addTextureToQueue({ queue, queueLOD, uuid: layer.normalMap, isAsset, size: 0, texture, object });
            else console.warn("Malformed Q3 saved, missing texture:", layer.normalMap)
        }
    }

    if (material) {
        validMaps.forEach(key => {
            if (material[key]) enqueTexture(material, key);

            data.slides.forEach(slide => {
                const anim = slide.animation[objectUuid];

                if (!anim) return;
                if (anim[key]) enqueTexture(anim, key);
            });
        });

        if (material.layers && material.materialType === "nested")
            material.layers.forEach(enqueLayer);
    }

}

export default loadTexture;
export { loadTexture, enqueueTextures, collectTextures, addBackgroundTextureToQueue };

/**
 * Loads texture.
 * @param {Object} props Parameter.
 * @param {Object} props.assetMap Asset map.
 * @param {Object} props.loaders Loaders.
 * @param {Object} props.assets Assets.
 * @param {Object} props.item Texture data.
 * @param {Object} props.data Project data.
 * @param {Object} props.background Background.
 * @return {Promise}
 */
function loadQueueTexture({ assetMap, loaders, assets, item, data, background }) {
    return new Promise((resolve, reject) => {
        function success(texture) {
            if (assets.textures[item.uuid])
                assets.textures[item.uuid].dispose();

            const textureData = data.assets.textures[item.uuid];

            Object.keys(textureData).forEach(key => {
                if (key === "repeat") Object.assign(texture.repeat, {
                    x: textureData[key][0],
                    y: textureData[key][1]
                });
                else texture[key] = textureData[key];
            });

            texture.uuid = item.uuid;
            assets.textures[item.uuid] = texture;

            afterLoad(texture);
        }

        function afterLoad(texture) {
            if (item.data.object) { updateObject({ data, assets, object: item.data.object }); }
            else { updateObjects({ data, assets }); }

            background.updateTextures();

            resolve(texture.image);
        }

        if (!(item.data.path in assetMap)) {
            assetMap[item.data.path] = new Promise((_resolve, _reject) => {
                loaders.image.load(item.data.path, image => _resolve([{}, image]), undefined, _reject);
            });
        }

        assetMap[item.data.path]
            .then(([texCache, image]) => {
                if (item.uuid in texCache) afterLoad(texCache[item.uuid]);
                else {
                    const texture = texCache[item.uuid] = new THREE.Texture(image);

                    success(texture);
                    texture.needsUpdate = true;
                }
            })
            .catch(reject);
    });
}

const loadCubeTexture = (function () {
    const whitePlaceholder = createImage("rgb(255,255,255)");
    /**
     * Load cube texture.
     * @param {Object} data Texture data.
     * @return {Promise}
     */
    return function loadCubeTexture(data) {
        const cube = new THREE.CubeTexture([
            whitePlaceholder, whitePlaceholder, whitePlaceholder,
            whitePlaceholder, whitePlaceholder, whitePlaceholder
        ]);

        Object.keys(data).forEach(key => {
            if (cube.hasOwnProperty(key) && key !== "PMREM")
                cube[key] = data[key];
        });

        whitePlaceholder.isReadyPromise.then(_ => cube.needsUpdate = true);

        return cube;
    }
})();

/**
 * Load regular texture.
 * @param {Object} data Texture data.
 * @return {Promise}
 */
function loadRegularTexture(data) {
    const fillStyle = data.dominantColor ? data.dominantColor : "rgb(120,120,120)";
    const img = createImage(fillStyle);
    const texture = new THREE.Texture(img);

    img.isReadyPromise.then(() => { texture.needsUpdate = true; });

    // assign properties like minfilter, etc
    Object.keys(data).forEach(k => {
        if (texture.hasOwnProperty(k)) {
            if (k === "repeat") {
                Object.assign(texture.repeat, { x: data[k][0], y: data[k][1] });
                texture.anisotropy = 4;
            } else texture[k] = data[k];
        }
    });

    return texture;
}

/**
 * Is property a texture map.
 * @param {String} propertyName Property name.
 */
function isTexture(propertyName) {
    return propertyName !== "aoMapIntensity" && regexMap.test(propertyName);
}

/**
 * Is texture in use.
 * @param {Object} props Parameter.
 * @param {Object} props.uuid Texture UUID.
 * @param {Object} props.data Project data.
 */
function isTextureInUse({ uuid, data }) {
    if (uuid.startsWith("lensflare") || !!data.assets.textures[uuid].cube)
        return true;

    function isAllowed(matKey) {
        return !(
            !Q3.editMode &&
            Q3.browserCapabilities.allowedMaps &&
            !Q3.browserCapabilities.allowedMaps.includes(matKey)
        );
    }

    function checkContainer(container) {
        if (Object.keys(container).some(key => {
            const material = container[key];
            if (material) {
                if (Object.keys(material).some(matKey => {
                    if (material[matKey] === uuid) return isAllowed(matKey);
                    else if (matKey === "layers" && material.materialType === "nested") {
                        return material[matKey].some(x => {
                            if (x.map && x.map === uuid) return true;
                            if (x.normalMap && x.normalMap === uuid) return true;
                            if (x.bumpMap && x.bumpMap === uuid) return true;

                            return false;
                        });
                    }

                    return false;
                })) return true;
            }
        })) return true;

        return false;
    }

    if (checkContainer(data.assets.materials)) return true;

    if (data.slides.some(slide => {
        if (checkContainer(slide.animation)) return true;

        return uuid === slide.background.value.image.texture
            || uuid === slide.background.value.image.textureMobile
            || uuid === slide.background.value.video;
    })) return true;

    console.warn(`Texture ${data.assets.textures[uuid].name} (${uuid}) is unused, removing.`);

    return false;
}

export { isTexture, isTextureInUse };

/**
 * Should the textur be ignored.
 * @param {String} textureUuid Texture UUID.
 * @param {THREE.Material[]} materials Material list.
 * @param {Object[]} slides Slide list,
 */
function shouldIgnoreTexture(textureUuid, materials, slides = []) {

    function shouldIgnoreTextureProperty(propertyName) {
        if (regexIntensity.test(propertyName)) { return true; }

        const capabilities = Q3.browserCapabilities.supports.pbr;

        if (!capabilities.roughness.enabled && propertyName === "roughnessMap" ||
            !capabilities.metalness.enabled && propertyName === "metalnessMap" ||
            !capabilities.ao.enabled && propertyName === "aoMap" ||
            !capabilities.normal.enabled && (propertyName === "normalMap" || propertyName === "bumpMap")) {
            return true;
        }

        return false;
    };

    let ignore = true;

    Object.keys(materials).forEach(key => {
        const mData = materials[key];

        Object.keys(mData).forEach(k => {
            if (mData[k] === textureUuid && isTexture(k) && !shouldIgnoreTextureProperty(k)) {
                ignore = false;
            }
        });
    });

    slides.forEach(slide => {
        if (
            slide.background.type === "texture"
            && (
                slide.background.value.video === textureUuid
                || slide.background.value.image.texture === textureUuid
                || slide.background.value.image.textureMobile === textureUuid
            )
        )
            ignore = false;
    });

    return ignore;
}

/**
 * Get texture URL.
 * @param {String} id Texture UUID.
 * @param {Object} data Texture data.
 * @param {number} size Texture size.
 */
function getTextureUrl(id, data, size) {
    const path = data.path.substr(0, data.path.lastIndexOf("."));
    const ext = data.path.substr(data.path.lastIndexOf(".") + 1);

    return `${Q3.s3Url}${path}${size > 0 && size < data.size ? ("_" + size) : ""}.${ext}${Q3.browserCapabilities.supports.WebP ? ".webp" : ""}`;

}

export { getTextureUrl };

/**
 * Updates object that uses material.
 * @param {Object} props Parameter.
 * @param {Object} props.data Project data.
 * @param {Object} props.assets Asset library.
 * @param {THREE.Mesh} props.object Object to which material belongs.
 */
function updateObject({ data, assets, object }) {
    for (let material of ensureArray(object.material))
        updateMaterial({
            key: material, assets,
            materialData: data.assets.materials[material]
        });
}

/**
 * Updates objects that uses material.
 * @param {Object} props Parameter.
 * @param {Object} props.data Project data.
 * @param {Object} props.assets Asset library.
 */
function updateObjects({ data, assets }) {
    Object.keys(data.assets.materials).forEach(key => {
        const materialData = data.assets.materials[key];

        if (!materialData) return;

        updateMaterial({ key, assets, materialData });
    });
}

/**
 * Update material.
 * @param {Object} props Parameter.
 * @param {String} props.key Material UUID.
 * @param {Object} props.assets Asset library.
 * @param {Object} props.materialData Material data.
 */
function updateMaterial({ key, assets, materialData }) {
    const material = assets.materials[key];

    Object.keys(materialData).forEach(key => {
        if (regexMap.test(key)
            && materialData[key] && key.indexOf("Intensity") === -1) {

            const texture = assets.textures[materialData[key]];

            if (!texture) {
                console.warn("Material " + materialData.name + " uses texture which is not present in textures collection");
                return;
            }

            if (key.indexOf("2") > -1) {//custom map uniforms for LOD
                material[key].value = assets.textures[materialData[key]];
                material[key].value.needsUpdate = true;
            } else if (key === "envMap") material[key] = assets.envLight.activeMap;
            else if (Q3.editMode || !Q3.browserCapabilities.allowedMaps || Q3.browserCapabilities.allowedMaps.includes(key)) {
                material[key] = assets.textures[materialData[key]];
                material[key].needsUpdate = true;
            }
        }
    });

    if (material.isMeshNestedMaterial) {
        material.layers.forEach((layer, i) => {
            if (materialData.layers[i].map)
                layer.map = assets.textures[materialData.layers[i].map];

            if (materialData.layers[i].normalMap)
                layer.normalMap = assets.textures[materialData.layers[i].normalMap];
        });

        material.resendTexturesToGPU();
    }

    material.needsUpdate = true;
    RenderManager.activeCamera.update = true;
}

const setDefaultNormalTexture = (function () {
    const image = createImage("rgb(128, 128, 255)");

    return function (texture) {
        texture.image = image;
        texture.needsUpdate = true;
    }
})();